import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { listDrivers, upsertDriver, deleteDriver } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  Users, Search, Plus, Pencil, Trash2, Upload, FileCheck2, Eye,
  RefreshCw, IdCard, FileText, Image as ImageIcon
} from 'lucide-react';

const emptyForm = {
  id: null,
  name: '',
  mobile: '',
  iqama_number: '',
  license_number: '',
  iqama_url: '',
  license_url: '',
  iqama_filename: '',
  license_filename: '',
  iqama_expiry: '',
  license_expiry: '',
};

const readAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const ExpiryBadge = ({ label, state, date }) => {
  const status = state?.status || 'unknown';
  const days = state?.days_left;
  const cls = {
    expired: 'bg-red-50 text-red-800 border-red-300',
    expiring: 'bg-amber-50 text-amber-800 border-amber-300',
    ok: 'bg-emerald-50 text-emerald-800 border-emerald-300',
    unknown: 'bg-slate-50 text-slate-500 border-slate-300',
  }[status];
  const suffix =
    status === 'expired' ? `${Math.abs(days)}d overdue`
    : status === 'expiring' ? `in ${days}d`
    : status === 'ok' ? (date ? `${date.slice(0,10)}` : '')
    : 'no date';
  return (
    <Badge variant="outline" className={`${cls} justify-start`}>
      {label}: {suffix}
    </Badge>
  );
};

const DocPreview = ({ url, filename, label }) => {
  if (!url) {
    return (
      <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-xs text-muted-foreground">
        No {label} on file
      </div>
    );
  }
  const isPdf = (filename || '').toLowerCase().endsWith('.pdf') || url.startsWith('data:application/pdf');
  if (isPdf) {
    return (
      <div className="rounded-md border p-3 bg-white">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="h-4 w-4 text-red-600" />
          <span className="text-xs font-medium">{filename || `${label}.pdf`}</span>
        </div>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-blue-600 underline"
          data-testid={`preview-${label}-open`}
        >
          Open PDF →
        </a>
      </div>
    );
  }
  return (
    <div className="rounded-md border bg-white p-2">
      <img
        src={url}
        alt={label}
        className="max-h-56 w-full object-contain rounded"
        data-testid={`preview-${label}-img`}
      />
      {filename && (
        <p className="text-[10px] text-muted-foreground mt-1 truncate">{filename}</p>
      )}
    </div>
  );
};

const DriverFormDialog = ({ open, onOpenChange, form, setForm, onSave, saving }) => {
  const iqamaInputRef = useRef(null);
  const licenseInputRef = useRef(null);
  const handleUpload = async (kind, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large (max 5MB)');
      return;
    }
    try {
      const dataUrl = await readAsDataUrl(file);
      if (kind === 'iqama') {
        setForm((p) => ({ ...p, iqama_url: dataUrl, iqama_filename: file.name }));
      } else {
        setForm((p) => ({ ...p, license_url: dataUrl, license_filename: file.name }));
      }
      toast.success(`${kind === 'iqama' ? 'Iqama' : 'License'} uploaded`);
    } catch {
      toast.error('Failed to read file');
    }
  };
  const isEdit = !!form.id;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Driver' : 'Add New Driver'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="driver-form-name"
              />
            </div>
            <div>
              <Label>Mobile</Label>
              <Input
                type="tel"
                value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                placeholder="+9665..."
                data-testid="driver-form-mobile"
              />
            </div>
            <div>
              <Label>Iqama Number *</Label>
              <Input
                value={form.iqama_number}
                onChange={(e) => setForm({ ...form, iqama_number: e.target.value })}
                placeholder="10-digit Iqama"
                data-testid="driver-form-iqama"
              />
            </div>
            <div>
              <Label>Driving License No.</Label>
              <Input
                value={form.license_number}
                onChange={(e) => setForm({ ...form, license_number: e.target.value })}
                data-testid="driver-form-license"
              />
            </div>
            <div>
              <Label>Iqama Expiry</Label>
              <Input
                type="date"
                value={form.iqama_expiry || ''}
                onChange={(e) => setForm({ ...form, iqama_expiry: e.target.value })}
                data-testid="driver-form-iqama-expiry"
              />
            </div>
            <div>
              <Label>License Expiry</Label>
              <Input
                type="date"
                value={form.license_expiry || ''}
                onChange={(e) => setForm({ ...form, license_expiry: e.target.value })}
                data-testid="driver-form-license-expiry"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Iqama Copy</Label>
              <input
                ref={iqamaInputRef}
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => handleUpload('iqama', e)}
                data-testid="driver-form-iqama-file"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={`w-full ${form.iqama_url ? 'border-emerald-400 text-emerald-800' : 'border-orange-300 text-orange-800'}`}
                onClick={() => iqamaInputRef.current?.click()}
              >
                {form.iqama_url ? <FileCheck2 className="h-4 w-4 mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}
                {form.iqama_url ? (form.iqama_filename || 'Uploaded') : 'Upload Iqama Photo/PDF'}
              </Button>
              <div className="mt-2">
                <DocPreview url={form.iqama_url} filename={form.iqama_filename} label="iqama" />
              </div>
            </div>
            <div>
              <Label>License Copy</Label>
              <input
                ref={licenseInputRef}
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => handleUpload('license', e)}
                data-testid="driver-form-license-file"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={`w-full ${form.license_url ? 'border-emerald-400 text-emerald-800' : 'border-orange-300 text-orange-800'}`}
                onClick={() => licenseInputRef.current?.click()}
              >
                {form.license_url ? <FileCheck2 className="h-4 w-4 mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}
                {form.license_url ? (form.license_filename || 'Uploaded') : 'Upload License Photo/PDF'}
              </Button>
              <div className="mt-2">
                <DocPreview url={form.license_url} filename={form.license_filename} label="license" />
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={onSave}
            disabled={saving || !form.name || !form.iqama_number}
            data-testid="driver-form-save"
          >
            {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add Driver')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DriverDetailDialog = ({ driver, open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{driver?.name || 'Driver'}</DialogTitle>
      </DialogHeader>
      {driver && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-muted-foreground">Mobile:</span> {driver.mobile || '—'}</div>
            <div><span className="text-muted-foreground">Iqama:</span> {driver.iqama_number}</div>
            <div><span className="text-muted-foreground">License No.:</span> {driver.license_number || '—'}</div>
            <div><span className="text-muted-foreground">Used on:</span> {driver.use_count || 0} DN(s)</div>
            <div><span className="text-muted-foreground">Last used:</span> {driver.last_used_at ? new Date(driver.last_used_at).toLocaleString() : '—'}</div>
            <div><span className="text-muted-foreground">Added:</span> {driver.created_at ? new Date(driver.created_at).toLocaleString() : '—'}</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <div>
              <p className="text-xs font-semibold mb-1 flex items-center gap-1">
                <IdCard className="h-3.5 w-3.5" /> Iqama Copy
              </p>
              <DocPreview url={driver.iqama_url} filename={driver.iqama_filename} label="iqama" />
            </div>
            <div>
              <p className="text-xs font-semibold mb-1 flex items-center gap-1">
                <IdCard className="h-3.5 w-3.5" /> License Copy
              </p>
              <DocPreview url={driver.license_url} filename={driver.license_filename} label="license" />
            </div>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export const DriversPage = () => {
  const { user } = useAuth();
  const canDelete = ['admin', 'operations', 'operation_manager'].includes(user?.role);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [detailDriver, setDetailDriver] = useState(null);

  const fetchDrivers = useCallback(async (q) => {
    try {
      setLoading(true);
      const res = await listDrivers(q);
      setDrivers(res.data || []);
    } catch {
      toast.error('Failed to load drivers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDrivers(); }, [fetchDrivers]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => fetchDrivers(search), 300);
    return () => clearTimeout(t);
  }, [search, fetchDrivers]);

  const openAdd = () => { setForm(emptyForm); setFormOpen(true); };
  const openEdit = (d) => {
    setForm({
      id: d.id,
      name: d.name || '',
      mobile: d.mobile || '',
      iqama_number: d.iqama_number || '',
      license_number: d.license_number || '',
      iqama_url: d.iqama_url || '',
      license_url: d.license_url || '',
      iqama_filename: d.iqama_filename || '',
      license_filename: d.license_filename || '',
      iqama_expiry: (d.iqama_expiry || '').slice(0, 10),
      license_expiry: (d.license_expiry || '').slice(0, 10),
    });
    setFormOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await upsertDriver({
        name: form.name.trim(),
        mobile: form.mobile.trim(),
        iqama_number: form.iqama_number.trim(),
        license_number: form.license_number.trim(),
        iqama_url: form.iqama_url,
        license_url: form.license_url,
        iqama_filename: form.iqama_filename,
        license_filename: form.license_filename,
        iqama_expiry: form.iqama_expiry || null,
        license_expiry: form.license_expiry || null,
      });
      toast.success(form.id ? 'Driver updated' : 'Driver added');
      setFormOpen(false);
      fetchDrivers(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (d) => {
    if (!window.confirm(`Delete driver "${d.name}"? This can't be undone.`)) return;
    try {
      await deleteDriver(d.id);
      toast.success('Driver deleted');
      fetchDrivers(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="space-y-6" data-testid="drivers-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Drivers Master</h1>
          <p className="text-muted-foreground">Reusable driver / operator profiles for delivery notes</p>
        </div>
        <Button onClick={openAdd} data-testid="add-driver-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Driver
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold">{drivers.length}</div>
            <p className="text-xs text-muted-foreground">Saved drivers</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-emerald-700">
              {drivers.filter(d => d.iqama_url && d.license_url).length}
            </div>
            <p className="text-xs text-muted-foreground">Fully documented</p>
          </CardContent>
        </Card>
        <Card className={drivers.some(d => d.iqama_expiry_status?.status === 'expiring' || d.license_expiry_status?.status === 'expiring') ? 'border-amber-300 bg-amber-50/40' : ''}>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-amber-700">
              {drivers.filter(d => d.iqama_expiry_status?.status === 'expiring' || d.license_expiry_status?.status === 'expiring').length}
            </div>
            <p className="text-xs text-muted-foreground">Expiring in ≤ 7 days</p>
          </CardContent>
        </Card>
        <Card className={drivers.some(d => d.iqama_expiry_status?.status === 'expired' || d.license_expiry_status?.status === 'expired') ? 'border-red-300 bg-red-50/40' : ''}>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-red-700">
              {drivers.filter(d => d.iqama_expiry_status?.status === 'expired' || d.license_expiry_status?.status === 'expired').length}
            </div>
            <p className="text-xs text-muted-foreground">Expired documents</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search name, Iqama, mobile, license…"
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="drivers-search-input"
              />
            </div>
            <Button variant="outline" onClick={() => fetchDrivers(search)}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Drivers ({drivers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading…</div>
          ) : drivers.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              No drivers yet. Add one manually, or one will appear here the next time you save an operator on a Delivery Note.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-3 font-medium">Name</th>
                    <th className="pb-3 font-medium">Mobile</th>
                    <th className="pb-3 font-medium">Iqama</th>
                    <th className="pb-3 font-medium">License</th>
                    <th className="pb-3 font-medium">Docs</th>
                    <th className="pb-3 font-medium">Expiry</th>
                    <th className="pb-3 font-medium">Used</th>
                    <th className="pb-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {drivers.map((d) => {
                    const iqOk = !!d.iqama_url;
                    const licOk = !!d.license_url;
                    return (
                      <tr key={d.id} className="border-b hover:bg-muted/40" data-testid={`driver-row-${d.id}`}>
                        <td className="py-3 font-medium">{d.name}</td>
                        <td className="py-3">{d.mobile || '—'}</td>
                        <td className="py-3">{d.iqama_number}</td>
                        <td className="py-3">{d.license_number || '—'}</td>
                        <td className="py-3">
                          <div className="flex gap-1">
                            <Badge
                              variant="outline"
                              className={iqOk ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : 'bg-orange-50 text-orange-800 border-orange-300'}
                            >
                              <IdCard className="h-3 w-3 mr-1" /> Iqama {iqOk ? '✓' : '—'}
                            </Badge>
                            <Badge
                              variant="outline"
                              className={licOk ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : 'bg-orange-50 text-orange-800 border-orange-300'}
                            >
                              <ImageIcon className="h-3 w-3 mr-1" /> Lic {licOk ? '✓' : '—'}
                            </Badge>
                          </div>
                        </td>
                        <td className="py-3">
                          <div className="flex flex-col gap-0.5 text-xs">
                            <ExpiryBadge label="Iqama" state={d.iqama_expiry_status} date={d.iqama_expiry} />
                            <ExpiryBadge label="Lic" state={d.license_expiry_status} date={d.license_expiry} />
                          </div>
                        </td>
                        <td className="py-3">
                          <Badge variant="outline">{d.use_count || 0}×</Badge>
                        </td>
                        <td className="py-3">
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDetailDriver(d)}
                              data-testid={`view-driver-${d.id}`}
                              title="Preview docs"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEdit(d)}
                              data-testid={`edit-driver-${d.id}`}
                              title="Edit"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            {canDelete && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600"
                                onClick={() => handleDelete(d)}
                                data-testid={`delete-driver-${d.id}`}
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <DriverFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        form={form}
        setForm={setForm}
        onSave={handleSave}
        saving={saving}
      />

      <DriverDetailDialog
        driver={detailDriver}
        open={!!detailDriver}
        onOpenChange={(v) => !v && setDetailDriver(null)}
      />
    </div>
  );
};

export default DriversPage;
